import { CollateralAssetResponse } from "../../codegen-api";
import { useTranslation } from "react-i18next";
import {
  COLORS,
  FIELD_COLORS,
  LAYER_COLORS,
} from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import {
  getAssetLogo,
  getAssetShortName,
  getCollateralAssets,
} from "../../utils/asset/assets";
import { BaseModal } from "../BaseModal";
import { InfoRow } from "../ConnectWalletModal/Deposit/style";
import SelectMultichainNetwork from "../SelectMultichainNetwork";
import { Chevron } from "../shared/Chevron/style";
import Dropdown from "../shared/Dropdown";
import { Input } from "../shared/Input";
import ModalOverlayInfo from "../shared/ModalOverlayInfo";
import { OverlayInfoContent } from "../shared/ModalOverlayInfo/style";
import { MaxButton } from "../shared/NumericField";
import {
  ButtonsContainer,
  DepositAssetDropdownItem,
  DepositAssetDropdownTitle,
  ErrorMessage,
  InputContainer,
  InputError,
  InputLabel,
  Spacer,
} from "./style";
import { IsUSDC } from "../../constants/addresses";
import { useWithdrawModal } from "./useWithdrawModal";
import {
  assetDisplayDecimals,
  formatFlooredAmount,
  invalidAmountMessage,
  withdrawAmountRules,
} from "./WithdrawModal.helpers";
import { formatKnownValue } from "../../utils/unknownNumbers";
import { IDepositWithdrawModalProps } from "./WithdrawModal.types";

function WithdrawModal({
  show,
  onHide,
  accountData,
  defaultAsset,
}: IDepositWithdrawModalProps) {
  const {
    t,
    formError,
    showHelpInfo,
    setShowHelpInfo,
    asset,
    assetDecimals,
    setAsset,
    isUnifiedVaultChain,
    selectedWithdrawalChain,
    setSelectedWithrawalChain,
    assetDropdownOpen,
    setAssetDropdownOpen,
    onMaxClick,
    loading,
    register,
    errors,
    notEnoughBridgeCapacityError,
    withdrawableBalance,
    withdrawableUnknown,
    withdrawableUnknownReason,
    collateralInfo,
    depositWithdrawError,
    submitButtonContent,
    handleSubmit,
    handleWithdraw,
  } = useWithdrawModal({ show, onHide, accountData, defaultAsset });
  const { t: inputsT } = useTranslation("app", { keyPrefix: "common.inputs" });
  // Copyable figures are floored at the asset's own display decimals.
  const displayDecimals = assetDisplayDecimals(asset);

  return (
    <BaseModal
      onHide={onHide}
      show={show}
      style={{
        width: 310,
      }}
      title={t("withdraw")}
    >
      <ModalOverlayInfo
        show={showHelpInfo}
        onClose={() => setShowHelpInfo(false)}
        height="280px"
      >
        <OverlayInfoContent>
          <span>{t("withdrawals")}</span>
          <span>
            {t("withdrawal_availability_desc_1", {
              asset: getAssetShortName(asset),
            })}
            {asset === CollateralAssetResponse.Weth
              ? t("eth_to_weth_desc")
              : " "}
            {t("withdrawal_availability_desc_2")}
          </span>
        </OverlayInfoContent>
      </ModalOverlayInfo>
      <form onSubmit={handleSubmit(handleWithdraw)}>
        <InputContainer>
          {!isUnifiedVaultChain && (
            <>
              <InputLabel>{t("withdraw_to")}</InputLabel>
              <SelectMultichainNetwork
                selectedChain={selectedWithdrawalChain}
                onSelectChain={setSelectedWithrawalChain}
              />
            </>
          )}
          <InputLabel>{t("amount")}</InputLabel>
          <Input
            placeholder="0.00"
            leftAccessory={
              <Dropdown
                onToggle={setAssetDropdownOpen}
                toggleStyles={{
                  padding: `0px ${SPACING.one}px`,
                  backgroundColor: FIELD_COLORS.two,
                }}
                dropdownMenuContainerStyles={{
                  overflow: "hidden",
                  backgroundColor: LAYER_COLORS.three,
                }}
                title={
                  <DepositAssetDropdownTitle>
                    <img
                      src={getAssetLogo(asset)}
                      width={24}
                      height={24}
                      alt={asset}
                    />
                    <Chevron
                      size="small"
                      direction={assetDropdownOpen ? "up" : "down"}
                    />
                  </DepositAssetDropdownTitle>
                }
                items={getCollateralAssets(
                  selectedWithdrawalChain,
                  "withdraw"
                ).map((v) => ({
                  label: (
                    <DepositAssetDropdownItem selected={v === asset}>
                      <img
                        src={getAssetLogo(v)}
                        width={24}
                        height={24}
                        alt={v}
                      />
                      <span>
                        {getAssetShortName(v, selectedWithdrawalChain)}
                      </span>
                    </DepositAssetDropdownItem>
                  ),
                  onSelect: () => {
                    setAsset(v);
                  },
                }))}
              />
            }
            rightAccessory={
              <MaxButton
                onClick={onMaxClick}
                type="button"
                disabled={withdrawableUnknown}
                title={
                  withdrawableUnknownReason
                    ? inputsT(`max_unavailable.${withdrawableUnknownReason}`)
                    : undefined
                }
              >
                {inputsT("max")}
              </MaxButton>
            }
            type="number"
            disabled={loading}
            {...register("amount", {
              required: true,
              validate: withdrawAmountRules(
                withdrawableBalance,
                withdrawableUnknown,
                assetDecimals
              ),
            })}
            error={
              Boolean(Object.keys(errors).length) ||
              !!notEnoughBridgeCapacityError
            }
          />
          {errors.amount?.type === "withdrawableKnown" && (
            <InputError>
              {t("withdrawable_unknown", {
                defaultValue:
                  "Withdrawable balance can't be calculated right now.",
              })}
            </InputError>
          )}
          {errors.amount?.type === "required" && (
            <InputError>{formError("amount.required")}</InputError>
          )}
          {errors.amount?.type === "validDecimal" && (
            <InputError>{invalidAmountMessage(t, assetDecimals)}</InputError>
          )}
          {errors.amount?.type === "moreThanZero" && (
            <InputError>{formError("amount.moreThanZero")}</InputError>
          )}
          {errors.amount?.type === "lessThanEqualToExchangeBalance" && (
            <InputError>
              {formError("amount.lessThanEqualToExchangeBalance")}
            </InputError>
          )}
          {!errors.amount?.type && !!notEnoughBridgeCapacityError && (
            <InputError>{notEnoughBridgeCapacityError}</InputError>
          )}
        </InputContainer>
        <InfoRow>
          <span>
            {t("exchange_balance")} ({getAssetShortName(asset)})
          </span>
          <span>
            {collateralInfo
              ? formatFlooredAmount(collateralInfo.balance, displayDecimals)
              : "---"}
          </span>
        </InfoRow>
        {IsUSDC(asset) && accountData?.credit && (
          <InfoRow>
            <span>
              {t("credit_balance")} ({getAssetShortName(asset)})
            </span>
            <span>
              {formatFlooredAmount(accountData.credit, displayDecimals)}
            </span>
          </InfoRow>
        )}
        <InfoRow>
          <span
            style={{
              color:
                errors.amount?.type === "lessThanEqualToExchangeBalance"
                  ? COLORS.negative.one
                  : undefined,
            }}
          >
            {t("withdrawable_balance")} (
            {getAssetShortName(asset, selectedWithdrawalChain)})
          </span>
          <span
            style={{
              color:
                errors.amount?.type === "lessThanEqualToExchangeBalance"
                  ? COLORS.negative.one
                  : undefined,
            }}
          >
            {withdrawableBalance && withdrawableBalance !== "0"
              ? formatFlooredAmount(withdrawableBalance, displayDecimals)
              : "---"}
          </span>
        </InfoRow>
        <InfoRow>
          <span>{t("total_exchange_equity")}</span>
          {/* null while a position is unpriced: "-", never "$0.00". */}
          <span>{formatKnownValue(accountData?.equity)}</span>
        </InfoRow>
        {/* <InfoRow>
          <span>{t("eta")}</span>
          <span>
            {ONCHAIN_ESTIMATES.withdrawal.value}{" "}
            {ONCHAIN_ESTIMATES.withdrawal.unit}
          </span>
        </InfoRow> */}
        <Spacer />
        {depositWithdrawError && (
          <ErrorMessage>{depositWithdrawError}</ErrorMessage>
        )}
        <ButtonsContainer>{submitButtonContent}</ButtonsContainer>
      </form>
    </BaseModal>
  );
}

export default WithdrawModal;
